// ./src/utils/api/PlayListService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { PlayListItem } from "./PlayListItemService";
import { ContentBlock } from "./ContentBlockService";
import { Display } from "./DisplayService";

// GraphQL queries
const PLAYLIST_LIST_QUERY = gql`
  query PlayListList($filter: PlayListFilter, $params: PaginationAndSorting) {
    playListList(filter: $filter, params: $params) {
      code
      data {
        id
        accountId
        name
        description
        blockTransitionAnimation
        progressStyle
        status
        createdBy
        updatedBy
        createdAt
        updatedAt
        rights
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PLAYLIST_GET_QUERY = gql`
  query PlayListGet($id: ID) {
    playListGet(id: $id) {
      code
      data {
        id
        accountId
        name
        description
        blockTransitionAnimation
        progressStyle
        status
        createdBy
        updatedBy
        createdAt
        updatedAt
        rights
        playListItems {
          id
          playListId
          contentBlockId
          orderNo
          status
        }
        contentBlocks {
          id
          venueId
          name
          description
          contentType
          contentData
          fileUrl
          uploadedFileId
          durationSeconds
          startDatetime
          endDatetime
          startTime
          endTime
          recurrencePattern
          recurrenceDays
          blockSettings
          orderNo
          status
        }
        displays {
          id
          themeId
          layoutId
          name
          description
          displayType
          roomId
          roomTypeId
          floor
          tag
          runningMessageId
          showPastHours
          showUpcomingHours
          emergencyContentBlockId
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PLAYLIST_GET_MANY_QUERY = gql`
  query PlayListGetMany($ids: [ID]) {
    playListGetMany(ids: $ids) {
      code
      data {
        id
        accountId
        name
        description
        blockTransitionAnimation
        progressStyle
        status
        createdBy
        updatedBy
        createdAt
        updatedAt
        rights
        playListItems {
          id
          playListId
          contentBlockId
          orderNo
          status
        }
        contentBlocks {
          id
          venueId
          name
          description
          contentType
          contentData
          fileUrl
          uploadedFileId
          durationSeconds
          startDatetime
          endDatetime
          startTime
          endTime
          recurrencePattern
          recurrenceDays
          blockSettings
          orderNo
          status
        }
        displays {
          id
          themeId
          layoutId
          name
          description
          displayType
          roomId
          roomTypeId
          floor
          tag
          runningMessageId
          showPastHours
          showUpcomingHours
          emergencyContentBlockId
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const PLAYLIST_CREATE_MUTATION = gql`
  mutation PlayListCreate($params: PlayListInput) {
    playListCreate(params: $params) {
      code
      data {
        id
        accountId
        name
        description
        blockTransitionAnimation
        progressStyle
        status
        createdBy
        updatedBy
        createdAt
        updatedAt
        rights
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PLAYLIST_UPDATE_MUTATION = gql`
  mutation PlayListUpdate($where: PlayListWhereInput, $params: PlayListInput) {
    playListUpdate(where: $where, params: $params) {
      code
      data {
        id
        accountId
        name
        description
        blockTransitionAnimation
        progressStyle
        status
        createdBy
        updatedBy
        createdAt
        updatedAt
        rights
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PLAYLIST_REMOVE_MUTATION = gql`
  mutation PlayListRemove($where: PlayListWhereInput) {
    playListRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PLAYLIST_REMOVE_MANY_MUTATION = gql`
  mutation PlayListRemoveMany($where: [PlayListWhereInput]) {
    playListRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface PlayList {
  id?: string | undefined;
  accountId?: string | undefined;
  name?: string | undefined;
  description?: string | undefined;
  blockTransitionAnimation?: string | null | undefined;
  progressStyle?: string | null | undefined;
  status?: number;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  rights?: string[];
  playListItems?: PlayListItem[];
  contentBlocks?: ContentBlock[];
  displays?: Display[];
  duplicateOfId?: string | null | undefined;
}

export interface PlayListInput {
  id?: string | null | undefined;
  name?: string | null | undefined;
  description?: string | null | undefined;
  blockTransitionAnimation?: string | null | undefined;
  progressStyle?: string | null | undefined;
  status?: number | null | undefined;
  duplicateOfId?: string | null | undefined;
}

export interface PlayListFilter {
  id?: string[] | null | undefined;
  name?: string | null | undefined;
  searchKeyword?: string | null | undefined;
  status?: number[] | null | undefined;
}

export interface PlayListWhereInput {
  id?: string | undefined;
}

/**
 * PlayList service that handles all playlist-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class PlayListService {
  /**
   * Get a list of playlists with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: PlayListFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(PLAYLIST_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single playlist by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(PLAYLIST_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple playlists by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(PLAYLIST_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new playlist
   */
  static async create(params: PlayListInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(PLAYLIST_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing playlist
   */
  static async update(
    where: PlayListWhereInput,
    params: PlayListInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(PLAYLIST_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete a playlist
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(PLAYLIST_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple playlists
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      PLAYLIST_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
